import { useCallback, useState } from "react";
import { evaluate } from "mathjs";
import { useFormulas } from "../../../hooks/query";
import { FORMULA_EVAL_ORDER } from "../../../constants/formulaEvalOrder";
import { resolveFormulaSubcategoryKey } from "../../../utils/formulaScope";

// Define measurements type based on actual usage (array of objects)
type MeasurementValues = any[];

interface PrefilledValues {
    [product: string]: {
        value: string;
        quantity?: number;
    };
}

export const sanitizeVariableName = (name: string): string => {
    if (!name) return "";
    return name
        .replace(/&/g, "And")
        .replace(/\//g, "Or")
        .replace(/[^a-zA-Z0-9_\s]/g, "")
        .replace(/\s+(\w)/g, (_, c) => c.toUpperCase())
        .replace(/\s+/g, "");
};

export const parseRoofPitchSq = (raw: any, totalArea: number): Record<string, number> => {
    const steepSquares = {
        SteepSquares7: 0,
        SteepSquares8: 0,
        SteepSquares9: 0,
        SteepSquares10: 0,
        SteepSquares11: 0,
        SteepSquares12: 0,
        SteepSquares13Plus: 0,
    };

    if (raw === null || raw === undefined || raw === "") return steepSquares;

    const parsePitchNumerator = (pitchStr: any): number | null => {
        if (pitchStr === null || pitchStr === undefined) return null;
        const str = String(pitchStr).trim();
        if (!str) return null;
        if (str.includes("/")) {
            const parts = str.split("/");
            const num = Number(parts[0].trim());
            if (!isNaN(num)) return num;
        }
        const num = Number(str);
        if (!isNaN(num)) return num;
        return null;
    };

    let parsed: any;
    if (typeof raw === "string") {
        try {
            parsed = JSON.parse(raw);
        } catch {
            parsed = null;
        }
    } else {
        parsed = raw;
    }

    if (Array.isArray(parsed)) {
        parsed.forEach((segment: any) => {
            if (!segment) return;
            const pitchNum = parsePitchNumerator(segment.pitch);
            const sq = Number(segment.squares) || 0;
            if (pitchNum !== null) {
                if (pitchNum === 7) steepSquares.SteepSquares7 += sq;
                else if (pitchNum === 8) steepSquares.SteepSquares8 += sq;
                else if (pitchNum === 9) steepSquares.SteepSquares9 += sq;
                else if (pitchNum === 10) steepSquares.SteepSquares10 += sq;
                else if (pitchNum === 11) steepSquares.SteepSquares11 += sq;
                else if (pitchNum === 12) steepSquares.SteepSquares12 += sq;
                else if (pitchNum >= 13) steepSquares.SteepSquares13Plus += sq;
            }
        });
    } else {
        const pitchNum = parsePitchNumerator(raw);
        if (pitchNum !== null) {
            if (pitchNum === 7) steepSquares.SteepSquares7 = totalArea;
            else if (pitchNum === 8) steepSquares.SteepSquares8 = totalArea;
            else if (pitchNum === 9) steepSquares.SteepSquares9 = totalArea;
            else if (pitchNum === 10) steepSquares.SteepSquares10 = totalArea;
            else if (pitchNum === 11) steepSquares.SteepSquares11 = totalArea;
            else if (pitchNum === 12) steepSquares.SteepSquares12 = totalArea;
            else if (pitchNum >= 13) steepSquares.SteepSquares13Plus = totalArea;
        }
    }

    return steepSquares;
};

export const parseChimneysSq = (raw: any): Record<string, number> => {
    const counts = {
        BrickChimneys: 0,
        WoodChimneys: 0,
    };
    if (raw === null || raw === undefined || raw === "") return counts;

    let parsed: any;
    if (typeof raw === "string") {
        try {
            parsed = JSON.parse(raw);
        } catch {
            parsed = null;
        }
    } else {
        parsed = raw;
    }

    if (Array.isArray(parsed)) {
        parsed.forEach((entry: any) => {
            if (!entry) return;
            const typeStr = String(entry.type || "").toLowerCase().trim();
            const count = Number(entry.count) || 0;
            if (typeStr === "brick") {
                counts.BrickChimneys += count;
            } else if (typeStr === "siding" || typeStr === "wood") {
                counts.WoodChimneys += count;
            }
        });
    }
    return counts;
};

const useFormulaEvaluator = () => {
    const [computedValues, setComputedValues] = useState<Record<string, number | null>>({});
    const { useList } = useFormulas();
    const { data: formulas, isLoading: isLoadingFormulas } = useList();

    const evaluateFormula = useCallback(
        (
            measurements: MeasurementValues,
            prefilledValues: PrefilledValues,
            attributes?: any[],
        ): Record<string, number | null> => {
            const results: Record<string, number | null> = {};

            // Return empty results if formulas are not loaded yet
            if (!formulas || isLoadingFormulas) {
                return results;
            }

            const orderedProducts = [
                ...FORMULA_EVAL_ORDER.filter((p) => p in formulas),
                ...Object.keys(formulas).filter((p) => !FORMULA_EVAL_ORDER.includes(p)),
            ];

            const measurementsObject = convertMeasurementIntoKeyValuePair(measurements);
            const totalArea = Number(measurementsObject.Area) || 0;
            const defaultChimneyCount = Number(measurementsObject.Chimney || measurementsObject.Chimneys) || 0;
            const attributesScope = convertAttributesIntoScope(attributes, totalArea, defaultChimneyCount);
            
            const selectionsScope: Record<string, string> = {};
            Object.entries(prefilledValues).forEach(([prod, data]: [string, any]) => {
                const key = sanitizeVariableName(prod) + "_Selection";
                selectionsScope[key] = data.systemLine || data.value;
            });

            for (const product of orderedProducts) {
                const productFormula = formulas[product];

                let expression: string | undefined;
                let outputFormat: "WholeNumber" | "Decimal" | undefined;

                if (productFormula.subcategories) {
                    const subKey = resolveFormulaSubcategoryKey(
                        product,
                        prefilledValues[product]?.value || "",
                        prefilledValues,
                    );
                    const subcategoryFormula = productFormula.subcategories[subKey];
                    if (subcategoryFormula) {
                        expression = subcategoryFormula.expression;
                        outputFormat = subcategoryFormula.outputFormat;
                    }
                }

                if (!expression && productFormula.expression) {
                    expression = productFormula.expression;
                    outputFormat = productFormula.outputFormat;
                }

                if (expression) {
                    try {
                        const resultsScope: Record<string, any> = {};
                        Object.entries(results).forEach(([prod, val]) => {
                            if (val !== null) {
                                resultsScope[sanitizeVariableName(prod)] = val;
                            }
                        });

                        const scope = {
                            ...measurementsObject,
                            ...attributesScope,
                            ...selectionsScope,
                            ...resultsScope,
                        };

                        let result = evaluate(expression, scope) as number;

                        if (outputFormat === "WholeNumber") {
                            result = Math.ceil(result);
                        }

                        results[product] = result;
                    } catch (error: any) {
                        if (error && error.message && error.message.includes("Undefined symbol")) {
                            // missing measurement — skip
                        } else {
                            console.error(`Error evaluating formula for ${product}:`, error);
                        }
                        results[product] = null;
                    }
                }
            }

            setComputedValues(results);
            return results; // Return computed values immediately
        },
        [formulas, isLoadingFormulas],
    );

    const convertMeasurementIntoKeyValuePair = (measurements: any) => {
        return measurements.reduce((acc: any, measurement: any) => {
            // Use ActualValue if available, otherwise fall back to CalculatedValue (predicted value)
            const value = measurement.ActualValue ?? measurement.CalculatedValue;

            // Try updating to number, but if it fails (NaN), keep as original string (unless it is empty/null)
            const numVal = Number(value);
            const sanitizedKey = sanitizeVariableName(measurement.MeasurementType);
            if (!isNaN(numVal) && value !== "" && value !== null && value !== undefined) {
                acc[sanitizedKey] = numVal;
            } else {
                acc[sanitizedKey] = value;
            }
            return acc;
        }, {});
    };

    /**
     * Converts inspection_attributes rows into a flat mathjs scope.
     * - Plain scalars ("6/12", "Yes", "1") → injected as-is (number if parseable)
     * - JSON arrays ([{size,count}]) → collapsed to total count (sum of all count fields)
     * Variable names are injected both raw ("Roof Pitch") and camelCase ("RoofPitch").
     */
    const convertAttributesIntoScope = (attributes?: any[], totalArea: number = 0, defaultChimneyCount: number = 0): Record<string, any> => {
        const scope: Record<string, any> = {
            SteepSquares7: 0,
            SteepSquares8: 0,
            SteepSquares9: 0,
            SteepSquares10: 0,
            SteepSquares11: 0,
            SteepSquares12: 0,
            SteepSquares13Plus: 0,
            BrickChimneys: 0,
            WoodChimneys: 0,
            HasEvaporativeCooler: 0,
        };
        if (!attributes || attributes.length === 0) return scope;

        let parsedChimneyDetails = false;

        attributes.forEach((attr) => {
            const rawName: string = attr.AttributeName || attr.Name || attr.key;
            if (!rawName) return;

            const sanitizedKey = sanitizeVariableName(rawName);
            // camelCase key: Remove special characters then "Roof Pitch" → "RoofPitch"
            const sanitizedName = rawName.replace(/[^a-zA-Z0-9\s]/g, '');
            const camelKey = sanitizedName.replace(/\s+(\w)/g, (_: string, c: string) => c.toUpperCase());

            const raw = attr.AttributeValue !== undefined ? attr.AttributeValue : (attr.Value !== undefined ? attr.Value : attr.value);
            if (raw === null || raw === undefined || raw === "") return;

            let value: any;
            if (rawName.toLowerCase() === "roof pitch") {
                const steepMap = parseRoofPitchSq(raw, totalArea);
                Object.assign(scope, steepMap);
            }

            if (rawName.toLowerCase() === "chimney details") {
                const chimneyMap = parseChimneysSq(raw);
                Object.assign(scope, chimneyMap);
                parsedChimneyDetails = true;
            }

            if (rawName.toLowerCase() === "has evaporative cooler") {
                value = (raw === "Yes") ? 1 : 0;
            } else {
                // Attempt JSON parse first (handles array attributes like [{size,count}])
                let parsed: any;
                try {
                    parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                } catch {
                    parsed = null;
                }

                if (Array.isArray(parsed)) {
                    // Sum all count fields, default to array length if no count field
                    const total = parsed.reduce((sum: number, item: any) => sum + (Number(item.count) || 0), 0);
                    value = total > 0 ? total : parsed.length;
                } else {
                    // Plain string — convert to number if possible, also parse fractions like "6/12"
                    let num = Number(raw);
                    if (isNaN(num) && typeof raw === "string" && raw.includes("/")) {
                        const parts = raw.split("/");
                        if (parts.length === 2) {
                            const num1 = Number(parts[0].trim());
                            const num2 = Number(parts[1].trim());
                            if (!isNaN(num1) && !isNaN(num2) && num2 !== 0) {
                                num = num1 / num2;
                            }
                        }
                    }
                    value = !isNaN(num) && raw !== "" ? num : raw;
                }
            }

            scope[sanitizedKey] = value;
            scope[camelKey] = value;
        });

        if (!parsedChimneyDetails) {
            const chimneyType = String(scope.ChimneyType || "").toLowerCase().trim();
            const chimneyCount = Number(scope.Chimney !== undefined ? scope.Chimney : defaultChimneyCount) || 0;
            if (chimneyCount > 0) {
                if (chimneyType === "brick") {
                    scope.BrickChimneys = chimneyCount;
                } else if (chimneyType === "siding" || chimneyType === "wood") {
                    scope.WoodChimneys = chimneyCount;
                }
            }
        }

        return scope;
    };

    return { computedValues, evaluateFormula };
};

export default useFormulaEvaluator;
